// Hook para marcar múltiples mensajes como leídos en batch
// Optimiza las llamadas a la API agrupando operaciones
// Evita múltiples llamadas individuales por cada mensaje
// Adaptado para web desde amarsi-app.

import { useCallback, useRef } from 'react';
import { chatService } from '../../services/chatService';

export function useBatchMarkAsRead() {
  // Referencia para tracking de mensajes pendientes
  const pendingMessages = useRef(new Set());
  const batchTimeout = useRef(null);
  const isProcessing = useRef(false);

  // Función para procesar batch de mensajes
  const processBatch = useCallback(async () => {
    if (isProcessing.current || pendingMessages.current.size === 0) {
      return;
    }

    isProcessing.current = true;
    const messagesToMark = Array.from(pendingMessages.current);
    pendingMessages.current.clear();

    console.log(`useBatchMarkAsRead: Procesando batch de ${messagesToMark.length} mensajes`);

    try {
      // Intentar usar batch si el servicio lo soporta
      if (chatService.markMultipleMessagesAsRead) {
        await chatService.markMultipleMessagesAsRead(messagesToMark);
        console.log(`useBatchMarkAsRead: Batch completado para ${messagesToMark.length} mensajes`);
      } else {
        // Fallback a llamadas individuales en paralelo
        const promises = messagesToMark.map(messageId => 
          chatService.markMessageAsRead(messageId)
        );
        await Promise.all(promises);
        console.log(`useBatchMarkAsRead: ${messagesToMark.length} mensajes marcados individualmente`);
      }
    } catch (error) {
      console.error('useBatchMarkAsRead: Error procesando batch:', error);
      // Re-add mensajes pendientes para reintentar
      messagesToMark.forEach(msgId => pendingMessages.current.add(msgId));
    } finally {
      isProcessing.current = false;
    }
  }, []);

  // Función para agregar mensaje al batch
  const markAsRead = useCallback((messageId) => {
    if (pendingMessages.current.has(messageId)) {
      return; // Ya está pendiente
    }

    pendingMessages.current.add(messageId);
    console.log(`useBatchMarkAsRead: Agregado mensaje ${messageId} al batch (total: ${pendingMessages.current.size})`);

    // Limpiar timeout existente
    if (batchTimeout.current) {
      clearTimeout(batchTimeout.current);
    }

    // Configurar nuevo timeout para procesar batch
    batchTimeout.current = setTimeout(() => {
      processBatch();
    }, 300); // 300ms para agrupar múltiples mensajes
  }, [processBatch]);

  // Función para forzar procesamiento inmediato del batch
  const flushBatch = useCallback(() => {
    if (batchTimeout.current) {
      clearTimeout(batchTimeout.current);
      batchTimeout.current = null;
    }
    processBatch();
  }, [processBatch]);

  // Cleanup al desmontar
  const cleanup = useCallback(() => {
    if (batchTimeout.current) {
      clearTimeout(batchTimeout.current);
    }
    processBatch(); // Procesar mensajes pendientes
  }, [processBatch]);

  return {
    markAsRead,
    flushBatch,
    cleanup,
    pendingCount: pendingMessages.current.size
  };
}
